// Per-frame material overrides on the bean rigs, applied only around the frame's render and restored
// right after, so the rig code never sees them: the one-frame white hit flash (§11.1, §2.13) and the
// BONK CAM killer highlight (§11.2: "killer outlined in their identity color").
//
// Rigs are reached only through the §24.2 BeanRig contract (`root`). Two ways in, per mesh:
//   1. Convention (preferred; works with custom vertex shaders such as merged, uniform-posed rigs):
//      a material that exposes `userData.flash` (an IUniform<number>, 0..1 toward white) gets it set
//      to 1 for the frame; an outline material that exposes `userData.outlineColor` (IUniform<Color>)
//      and optionally `userData.outlineScale` (IUniform<number>) is recolored and thickened.
//   2. Fallback for materials with no custom shader code: a lit surface is swapped for a white basic
//      material; a MaterialLibrary hull (same vertex shader as `lib.outlineMaterial`) for a thicker
//      identity-colored hull.
// Anything else is left alone: swapping a custom shader would lose its vertex transform.
import { BackSide, Color, Mesh, MeshBasicMaterial, ShaderMaterial, type Material, type Object3D } from 'three';
import type { MaterialLibrary } from '../materials/library';

const FLASH_COLOR = '#FFFFFF';
/** Killer outline width (m): thicker than the 0.025 m bean outline so it reads in the replay. */
const KILLER_OUTLINE = 0.06;
const KILLER_SCALE = 2.4;

interface NumUniform { value: number }
interface ColorUniform { value: Color }

function numUniform(m: Material, key: string): NumUniform | null {
  const u = (m.userData as Record<string, unknown>)[key] as NumUniform | undefined;
  return u && typeof u.value === 'number' ? u : null;
}

function colorUniform(m: Material, key: string): ColorUniform | null {
  const u = (m.userData as Record<string, unknown>)[key] as ColorUniform | undefined;
  return u && u.value instanceof Color ? u : null;
}

/** True when the material runs its own shader code (swapping it would lose the vertex transform). */
function customShader(m: Material): boolean {
  return m instanceof ShaderMaterial || Object.prototype.hasOwnProperty.call(m, 'onBeforeCompile')
    || Object.prototype.hasOwnProperty.call(m, 'customProgramCacheKey');
}

export class RigFx {
  private readonly white = new MeshBasicMaterial({ color: new Color(FLASH_COLOR), toneMapped: false });
  private readonly swapped: Mesh[] = [];
  private readonly saved: (Material | Material[])[] = [];
  private readonly nums: NumUniform[] = [];
  private readonly numSaved: number[] = [];
  private readonly colors: ColorUniform[] = [];
  private readonly colorSaved: Color[] = [];
  private readonly colorPool: Color[] = [];
  private readonly outlineCache = new Map<string, Material>();
  private readonly tmp = new Color();
  private hullVertex: string | null = null;

  constructor(private readonly lib: MaterialLibrary) {}

  /** Turns every lit surface of `root` white (outlines stay ink). */
  flash(root: Object3D): void {
    root.traverseVisible((o) => {
      if (!(o instanceof Mesh) || Array.isArray(o.material)) return;
      const m = o.material as Material;
      const u = numUniform(m, 'flash');
      if (u) {
        this.setNum(u, 1);
        return;
      }
      if (m.side === BackSide || (m.transparent && !m.depthWrite) || customShader(m)) return;
      this.swap(o, this.white);
    });
  }

  /** Recolors `root`'s outline hulls in `hex`, thicker (the BONK CAM killer). */
  outline(root: Object3D, hex: string): void {
    this.tmp.set(hex);
    root.traverseVisible((o) => {
      if (!(o instanceof Mesh) || Array.isArray(o.material)) return;
      const m = o.material as Material;
      const c = colorUniform(m, 'outlineColor');
      if (c) {
        this.setColor(c, this.tmp);
        const w = numUniform(m, 'outlineScale');
        if (w) this.setNum(w, w.value * KILLER_SCALE);
        return;
      }
      if (m.side === BackSide && this.libraryHull(m)) this.swap(o, this.outlineFor(hex));
    });
  }

  /** Puts every override back (call after the frame's render). */
  restore(): void {
    for (let i = 0; i < this.swapped.length; i++) this.swapped[i].material = this.saved[i];
    this.swapped.length = 0;
    this.saved.length = 0;
    for (let i = 0; i < this.nums.length; i++) this.nums[i].value = this.numSaved[i];
    this.nums.length = 0;
    this.numSaved.length = 0;
    for (let i = 0; i < this.colors.length; i++) {
      this.colors[i].value.copy(this.colorSaved[i]);
      this.colorPool.push(this.colorSaved[i]);
    }
    this.colors.length = 0;
    this.colorSaved.length = 0;
  }

  get pending(): number {
    return this.swapped.length + this.nums.length + this.colors.length;
  }

  /** A hull built by the MaterialLibrary (its standard extrusion), or a plain back-side material. */
  private libraryHull(m: Material): boolean {
    if (!customShader(m)) return true;
    if (!(m instanceof ShaderMaterial)) return false;
    if (this.hullVertex === null) {
      const ref = this.lib.outlineMaterial(KILLER_OUTLINE);
      this.hullVertex = ref instanceof ShaderMaterial ? ref.vertexShader : '';
    }
    return this.hullVertex !== '' && m.vertexShader === this.hullVertex;
  }

  private outlineFor(hex: string): Material {
    let mat = this.outlineCache.get(hex);
    if (!mat) {
      mat = this.lib.outlineMaterial(KILLER_OUTLINE, hex);
      this.outlineCache.set(hex, mat);
    }
    return mat;
  }

  private swap(mesh: Mesh, mat: Material): void {
    this.swapped.push(mesh);
    this.saved.push(mesh.material);
    mesh.material = mat;
  }

  private setNum(u: NumUniform, v: number): void {
    if (this.nums.includes(u)) return;
    this.nums.push(u);
    this.numSaved.push(u.value);
    u.value = v;
  }

  private setColor(u: ColorUniform, c: Color): void {
    if (this.colors.includes(u)) return;
    const saved = this.colorPool.pop() ?? new Color();
    saved.copy(u.value);
    this.colors.push(u);
    this.colorSaved.push(saved);
    u.value.copy(c);
  }
}
